import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { can, type Me } from '../auth/me';
import { dotColor } from '../contractors/colors';
import type { DayDot, DayExtra } from '../events/CalendarViews';
import { useAssignedTasks } from './access';
import { formatDue, isOverdue, loadTaskLayer, saveTaskLayer, TASKS_KEY, TASKS_RESOURCE, type Task } from './types';
import './tasks.css';

/**
 * The calendar layer "Zadania firmowe" (P7): for roles that may see tasks and for anybody with an open task given to
 * them; on or off is remembered per user in this browser (a layer, not a filter). Switched in the calendar's
 * "Status i widok" field, under "Pokaż też" (owner 20.10 23:37).
 */
export function useTaskLayer(me: Me | null | undefined) {
  const assigned = useAssignedTasks(me);
  const available = !!me && (can(me, TASKS_RESOURCE, 'view') || assigned);
  const [on, setOn] = useState(() => (me ? loadTaskLayer(me.id) : false));
  return {
    available,
    on: available && on,
    toggle: () => {
      if (!me) return;
      setOn((v) => {
        saveTaskLayer(me.id, !v);
        return !v;
      });
    },
  };
}

/** The tasks due in the shown days (done ones too), when the layer is on. */
export function useCalendarTasks(range: { from: string; to: string } | null, enabled: boolean): Task[] {
  const q = useQuery({
    queryKey: [...TASKS_KEY, 'calendar', range?.from ?? '', range?.to ?? ''],
    enabled: enabled && range !== null,
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: () => api<{ tasks: Task[] }>(`/api/company-tasks?status=all&from=${range!.from}&to=${range!.to}`),
  });
  return enabled ? (q.data?.tasks ?? []) : [];
}

function TaskMark({ task, today, onOpen }: { task: Task; today: string; onOpen: (id: string) => void }) {
  const overdue = isOverdue(task, today);
  return (
    <button
      type="button"
      className={`task-mark${task.done ? ' done' : ''}${overdue ? ' overdue' : ''}`}
      aria-label={taskLabel(task, today)}
      title={`${task.title}${task.assigneeName ? ` — ${task.assigneeName}` : ''}`}
      onClick={() => onOpen(task.id)}
    >
      <span className="task-mark-name">{task.title}</span>
    </button>
  );
}

/** What a task says to a screen reader: "Zadanie: <title>, <due>, <state>". */
function taskLabel(task: Task, today: string): string {
  const state = task.done ? 'zrobione' : isOverdue(task, today) ? 'zaległe' : 'do zrobienia';
  return `Zadanie: ${task.title}, ${formatDue(task)}, ${state}`;
}

/**
 * The layer for the calendar views: diamonds on the due day (the week's head, the phone), a click opens the task; in
 * the month a dot row (owner 2026-10-29 16:02: „wszystkie zadania … są kropkami”) — grey, a task has no client.
 */
export function taskDayExtra(tasks: Task[], today: string, onOpen: (id: string) => void): DayExtra {
  const byDay = new Map<string, Task[]>();
  for (const t of tasks) byDay.set(t.dueDay, [...(byDay.get(t.dueDay) ?? []), t]);
  return {
    has: (day) => byDay.has(day),
    marks: (day) => {
      const list = byDay.get(day);
      if (!list) return null;
      return (
        <span className="task-marks">
          {list.map((t) => (
            <TaskMark key={t.id} task={t} today={today} onOpen={onOpen} />
          ))}
        </span>
      );
    },
    dots: (day) =>
      (byDay.get(day) ?? []).map(
        (t): DayDot => ({
          key: t.id,
          time: t.dueTime ?? '',
          name: t.title,
          color: undefined,
          label: taskLabel(t, today),
          title: `Zadanie: ${t.title}${t.assigneeName ? ` — ${t.assigneeName}` : ''}`,
          done: t.done,
          overdue: isOverdue(t, today),
          onOpen: () => onOpen(t.id),
        }),
      ),
  };
}

/** The key under the calendar, drawn like a mark (`dot`: the month, where a task is a grey dot — owner 2026-10-29). */
export function TaskLegendKey({ dot = false }: { dot?: boolean }) {
  return (
    <span className="cal-key">
      {dot ? <span className="cal-dot" aria-hidden="true" style={{ background: dotColor(null) }} /> : <span className="task-mark" aria-hidden="true" />}
      Zadanie firmowe
    </span>
  );
}
